/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import Button from 'components/atoms/Button'
import Input from 'components/atoms/Input'
import {
  useSearchV1OrdersQuery,
  useSearchV1ReportsQuery,
  useSearchV1PhysiciansQuery,
  useLazyGetV1ReportDownloadUrlQuery,
} from 'store/services/v1Archive'
import routes from 'components/lab/constants/routes'

type Tab = 'orders' | 'reports' | 'physicians'

const PAGE_SIZE = 25

const V1ArchivePage = () => {
  const [activeTab, setActiveTab] = useState<Tab>('orders')
  const [orderId, setOrderId] = useState('')
  const [patientName, setPatientName] = useState('')
  const [reportId, setReportId] = useState('')
  const [reportOrderId, setReportOrderId] = useState('')
  const [physicianQ, setPhysicianQ] = useState('')
  const [physicianNpi, setPhysicianNpi] = useState('')
  const [ordersPage, setOrdersPage] = useState(0)
  const [reportsPage, setReportsPage] = useState(0)
  const [physiciansPage, setPhysiciansPage] = useState(0)

  useEffect(() => {
    setOrdersPage(0)
  }, [orderId, patientName])
  useEffect(() => {
    setReportsPage(0)
  }, [reportId, reportOrderId])
  useEffect(() => {
    setPhysiciansPage(0)
  }, [physicianQ, physicianNpi])

  const ordersParams = {
    orderId: orderId.trim() || undefined,
    patientName: patientName.trim() || undefined,
    limit: PAGE_SIZE,
    offset: ordersPage * PAGE_SIZE,
  }
  const reportsParams = {
    reportId: reportId.trim() || undefined,
    orderId: reportOrderId.trim() || undefined,
    limit: PAGE_SIZE,
    offset: reportsPage * PAGE_SIZE,
  }
  const physiciansParams = {
    q: physicianQ.trim() || undefined,
    npi: physicianNpi.trim() || undefined,
    limit: PAGE_SIZE,
    offset: physiciansPage * PAGE_SIZE,
  }

  const { data: ordersData, isLoading: ordersLoading } = useSearchV1OrdersQuery(
    activeTab === 'orders' ? ordersParams : { limit: 1 },
    { skip: activeTab !== 'orders' },
  )
  const { data: reportsData, isLoading: reportsLoading } = useSearchV1ReportsQuery(
    activeTab === 'reports' ? reportsParams : { limit: 1 },
    { skip: activeTab !== 'reports' },
  )
  const { data: physiciansData, isLoading: physiciansLoading } = useSearchV1PhysiciansQuery(
    activeTab === 'physicians' ? physiciansParams : { limit: 1 },
    { skip: activeTab !== 'physicians' },
  )

  const orders = ordersData?.items ?? []
  const ordersTotal = ordersData?.total ?? 0
  const reports = reportsData?.items ?? []
  const reportsTotal = reportsData?.total ?? 0
  const physicians = physiciansData?.items ?? []
  const physiciansTotal = physiciansData?.total ?? 0

  const [getDownloadUrl, { isLoading: downloadLoading }] = useLazyGetV1ReportDownloadUrlQuery()

  const handleDownloadPdf = async (id: number) => {
    try {
      const { data } = await getDownloadUrl(id)
      if (data?.url) window.open(data.url, '_blank')
    } catch {
      // Error handled by RTK
    }
  }

  const formatDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—')

  const PaginationBar = ({
    page,
    pageSize,
    count,
    total,
    onPrev,
    onNext,
  }: {
    page: number
    pageSize: number
    count: number
    total: number
    onPrev: () => void
    onNext: () => void
  }) => (
    <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
      <span className="text-muted small">
        Showing {count === 0 ? 0 : page * pageSize + 1}–{page * pageSize + count} of {total}
      </span>
      <div className="d-flex gap-1">
        <Button
          size="sm"
          variant="secondary"
          disabled={page === 0}
          onClick={onPrev}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={(page + 1) * pageSize >= total}
          onClick={onNext}
        >
          Next
        </Button>
      </div>
    </div>
  )

  return (
    <DashboardContent
      title="V1 Archive"
      icon="fas fa-archive"
      breadcrumb={[{ label: 'V1 Archive', to: `${routes.index}${routes.v1Archive.root}` }]}
      content={
        <>
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'orders' ? 'active' : ''}`}
                onClick={() => setActiveTab('orders')}
              >
                Orders
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'reports' ? 'active' : ''}`}
                onClick={() => setActiveTab('reports')}
              >
                Reports
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'physicians' ? 'active' : ''}`}
                onClick={() => setActiveTab('physicians')}
              >
                Physicians
              </button>
            </li>
          </ul>

          {activeTab === 'orders' && (
            <div className="card card-outline card-primary">
              <div className="card-header">
                <h3 className="card-title">Search V1 Orders</h3>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-2">
                    <label className="form-label">Order ID</label>
                    <Input
                      type="text"
                      className="form-control"
                      value={orderId}
                      handleChange={(e) => setOrderId(e.target.value)}
                      placeholder="Order ID"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Patient Name</label>
                    <Input
                      type="text"
                      className="form-control"
                      value={patientName}
                      handleChange={(e) => setPatientName(e.target.value)}
                      placeholder="Patient name"
                    />
                  </div>
                </div>
                {ordersLoading && <p className="text-muted">Loading...</p>}
                <div className="table-responsive">
                  <table className="table table-sm table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Patient</th>
                        <th>Physician</th>
                        <th>Fax</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => (
                        <tr key={o.orderId}>
                          <td>{o.orderId}</td>
                          <td>{o.patientName ?? '—'}</td>
                          <td>{o.physicianName ?? '—'}</td>
                          <td>{o.physicianFax ?? '—'}</td>
                          <td>{o.status ?? '—'}</td>
                          <td>{formatDate(o.created)}</td>
                          <td>{formatDate(o.completed)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!ordersLoading && orders.length === 0 && (
                  <p className="text-muted">No orders found. Enter search criteria above.</p>
                )}
                <PaginationBar
                  page={ordersPage}
                  pageSize={PAGE_SIZE}
                  count={orders.length}
                  total={ordersTotal}
                  onPrev={() => setOrdersPage((p) => Math.max(0, p - 1))}
                  onNext={() => setOrdersPage((p) => p + 1)}
                />
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="card card-outline card-primary">
              <div className="card-header">
                <h3 className="card-title">Search V1 Reports</h3>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-2">
                    <label className="form-label">Report ID</label>
                    <Input
                      type="text"
                      className="form-control"
                      value={reportId}
                      handleChange={(e) => setReportId(e.target.value)}
                      placeholder="Report ID"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Order ID</label>
                    <Input
                      type="text"
                      className="form-control"
                      value={reportOrderId}
                      handleChange={(e) => setReportOrderId(e.target.value)}
                      placeholder="Order ID"
                    />
                  </div>
                </div>
                {reportsLoading && <p className="text-muted">Loading...</p>}
                <div className="table-responsive">
                  <table className="table table-sm table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>Report ID</th>
                        <th>Order ID</th>
                        <th>Patient</th>
                        <th>Physician</th>
                        <th>Result</th>
                        <th>Created</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((r) => (
                        <tr key={r.reportId}>
                          <td>{r.reportId}</td>
                          <td>{r.orderId}</td>
                          <td>{r.patientName ?? '—'}</td>
                          <td>{r.physicianName ?? '—'}</td>
                          <td>{r.resultQualification ?? r.resultCode ?? '—'}</td>
                          <td>{formatDate(r.created)}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={downloadLoading}
                              onClick={() => handleDownloadPdf(r.reportId)}
                            >
                              Download PDF
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!reportsLoading && reports.length === 0 && (
                  <p className="text-muted">No reports found. Enter search criteria above.</p>
                )}
                <PaginationBar
                  page={reportsPage}
                  pageSize={PAGE_SIZE}
                  count={reports.length}
                  total={reportsTotal}
                  onPrev={() => setReportsPage((p) => Math.max(0, p - 1))}
                  onNext={() => setReportsPage((p) => p + 1)}
                />
              </div>
            </div>
          )}

          {activeTab === 'physicians' && (
            <div className="card card-outline card-primary">
              <div className="card-header">
                <h3 className="card-title">Search V1 Physicians</h3>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Name or NPI</label>
                    <Input
                      type="text"
                      className="form-control"
                      value={physicianQ}
                      handleChange={(e) => setPhysicianQ(e.target.value)}
                      placeholder="Search by name or NPI"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">NPI</label>
                    <Input
                      type="text"
                      className="form-control"
                      value={physicianNpi}
                      handleChange={(e) => setPhysicianNpi(e.target.value)}
                      placeholder="NPI"
                    />
                  </div>
                </div>
                {physiciansLoading && <p className="text-muted">Loading...</p>}
                <div className="table-responsive">
                  <table className="table table-sm table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>Physician ID</th>
                        <th>NPI</th>
                        <th>Name</th>
                        <th>Fax</th>
                        <th>Phone</th>
                        <th>Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {physicians.map((p) => (
                        <tr key={p.physicianId}>
                          <td>{p.physicianId}</td>
                          <td>{p.npi ?? '—'}</td>
                          <td>{p.name ?? '—'}</td>
                          <td>{p.fax ?? '—'}</td>
                          <td>{p.phone ?? '—'}</td>
                          <td>
                            {[p.address, p.city, p.state, p.zipCode].filter(Boolean).join(', ') || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!physiciansLoading && physicians.length === 0 && (
                  <p className="text-muted">No physicians found. Enter search criteria above.</p>
                )}
                <PaginationBar
                  page={physiciansPage}
                  pageSize={PAGE_SIZE}
                  count={physicians.length}
                  total={physiciansTotal}
                  onPrev={() => setPhysiciansPage((p) => Math.max(0, p - 1))}
                  onNext={() => setPhysiciansPage((p) => p + 1)}
                />
              </div>
            </div>
          )}
        </>
      }
    />
  )
}

export default V1ArchivePage
